import { Button } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import type { SurveyFactorialFormSchema } from 'modules/surveys/components/Settings/CycleConfiguration'
import { frequencyOptionLabel } from 'modules/surveys/components/Settings/CycleOptions'
import { getTimeInUserTimeFormat } from 'modules/surveys/lib/getTimeInUserTimeFormat'
import type Survey from 'modules/surveys/store/models/v2/Survey'

interface RestartSurveyModalProps {
  survey: Survey
  onClose: () => void
  surveyData: SurveyFactorialFormSchema
}

const RestartSurveyModal = ({ survey, onClose, surveyData }: RestartSurveyModalProps) => {
  const { access } = useCurrentSession()
  const daysToAnswer = surveyData.duration_in_days
    ? surveyData.duration_in_days
    : i18n
        .t('surveys.form.settings.cycle_configuration.frequency.days_to_answer.placeholder')
        .toLowerCase()

  const startDateTimeLocal = moment(surveyData.start_on + ' ' + surveyData.scheduled_time).local()
  const isFutureStartDate = moment().local().isBefore(startDateTimeLocal)

  const descriptionValues = {
    start_on: surveyData.start_on,
    start_at: getTimeInUserTimeFormat(surveyData.scheduled_time, access.timeFormat !== 24),
    days_to_answer: daysToAnswer,
  }

  const description = isFutureStartDate
    ? survey.hasSchedule
      ? i18n.t('surveys.form.restart.modal.description_future_start_day', {
          ...descriptionValues,
          frequency_option: frequencyOptionLabel().get(surveyData.frequency)?.toLowerCase(),
        })
      : i18n.t('surveys.form.restart.modal.description_future_start_day_single', descriptionValues)
    : i18n.t('surveys.form.restart.modal.description')

  const { running, performAction: restartSurvey } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      await survey.startSurvey()
      survey.fetch()
      closePortal()
    },
    {
      successMessage: i18n.t('common.saved_successfully'),
    }
  )

  return (
    <Modal
      illustration="calendar-clock"
      title={i18n.t('surveys.form.restart.modal.title')}
      description={description}
      onClose={onClose}
      onSubmit={restartSurvey}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={running} submit>
            {i18n.t('common.continue')}
          </Button>
        </>
      }
    />
  )
}

export default observer(RestartSurveyModal)
